/**
 * Trainer Portal — verified pro dashboard with profile editor, availability,
 * messages, and "My Clients" (nutritionist only).
 *
 * Route: /pro/portal
 *
 * Now handles 4 states returned by GET /api/pro/portal:
 *   - status='not_applied'  → shows "Apply to become a verified pro" CTA
 *   - status='pending'      → shows "Application under review" screen
 *   - status='rejected'     → shows rejection reason + re-apply CTA
 *   - status='verified'     → shows the full tabbed dashboard
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, BadgeCheck, Clock, XCircle, UserPlus, ShoppingBag, Camera, Image as ImageIcon } from "lucide-react";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import { compressImage } from "../../utils/imageCompress";
import { detectContactLeak } from "../../utils/contactFilter";
import ContactLeakBanner from "../../components/chat/ContactLeakBanner";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function TrainerPortal() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return t || "dashboard";
    } catch {
      return "dashboard";
    }
  }); // dashboard | profile | availability | bookings | plans | community | messages | clients

  const refresh = () =>
    apiClient
      .get("/pro/portal")
      .then((r) => {
        setData(r.data);
        setLoadError(false);
      })
      .catch(() => {
        setLoadError(true);
        setData(null);
      });
  useEffect(() => {
    refresh();
  }, []);

  if (!data && !loadError) {
    return (
      <div className="app-page">
        <TopBar title="Trainer Portal" back />
        <main className="px-4 pt-10 text-center text-ink-soft" data-testid="portal-loading">
          Loading…
        </main>
        <BottomNav />
      </div>
    );
  }

  if (loadError) {
    return (
      <StatusScreen
        testid="portal-error"
        icon={<XCircle size={34} color="#E5484D" />}
        iconBorder="#E5484D"
        title="Couldn't load portal"
        body="Check your connection and try again."
        primary={{ label: "Retry", onClick: refresh }}
      />
    );
  }

  const status = data.status;

  if (status === "not_applied") {
    return (
      <StatusScreen
        testid="portal-not-applied"
        icon={<UserPlus size={34} color="#00D4FF" />}
        iconBorder="#00D4FF"
        title="Become a verified pro"
        body="Apply as a trainer or nutritionist to unlock the Trainer Portal — get discovered by thousands of members, manage bookings, and message clients."
        primary={{ label: "Apply now", onClick: () => navigate("/pro/apply") }}
      />
    );
  }

  if (status === "pending") {
    return (
      <StatusScreen
        testid="portal-pending"
        icon={<Clock size={34} color="#C08400" />}
        iconBorder="#C08400"
        title="Under review"
        body={`Your ${data.application?.role || "professional"} application was submitted on ${(
          data.application?.submitted_at || ""
        ).slice(0, 10)} and is being reviewed. We'll notify you within 24 hours.`}
        primary={{ label: "Back to home", onClick: () => navigate("/home") }}
        secondary={{ label: "Edit application", onClick: () => navigate("/pro/apply") }}
      />
    );
  }

  if (status === "rejected") {
    return (
      <StatusScreen
        testid="portal-rejected"
        icon={<XCircle size={34} color="#E5484D" />}
        iconBorder="#E5484D"
        title="Application not approved"
        body={
          data.rejection_reason ||
          data.application?.rejection_reason ||
          "Please ensure your certificate is valid, clearly readable, and matches the details you submitted."
        }
        primary={{
          label: "Fix and resubmit",
          onClick: () => navigate("/pro/apply", { state: { resumeAt: 3 } }),
        }}
        secondary={{
          label: "Contact support",
          onClick: () =>
            (window.location.href =
              "mailto:support@fitfrik.com?subject=Professional%20Application%20Help"),
        }}
      />
    );
  }

  // status === 'verified' → render full dashboard
  const isNutritionist = data.profile?.role === "nutritionist";

  return (
    <div className="app-page" data-testid="trainer-portal">
      <header className="sticky top-0 z-20 bg-canvas border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="portal-back"
            className="-ml-2 w-10 h-10 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-display text-[20px]">My Trainer Portal</h1>
          <span
            className="ml-2 inline-flex items-center gap-1.5 px-3 h-7 text-[11px] font-extrabold uppercase tracking-wider"
            style={{
              background: "linear-gradient(135deg, #00D4FF 0%, #0FB5A6 100%)",
              color: "#0B0B0B",
              borderRadius: 100,
              boxShadow: "0 3px 10px rgba(0,212,255,0.35)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: "0.12em",
            }}
            data-testid="trainer-verified-badge"
          >
            <BadgeCheck size={13} strokeWidth={2.6} /> Certified Pro
          </span>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto" data-testid="portal-tabs">
          {[
            { id: "dashboard", label: "Dashboard" },
            { id: "profile", label: "Profile" },
            { id: "availability", label: "Availability" },
            { id: "bookings", label: "Bookings" },
            { id: "plans", label: "Plans" },
            { id: "ai-tasks", label: "AI Tasks" },
            { id: "arena", label: "Arena" },
            { id: "community", label: "Community" },
            { id: "messages", label: "Messages" },
            ...(isNutritionist ? [{ id: "clients", label: "My Clients" }] : []),
          ].map((t) => {
            const on = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`portal-tab-${t.id}`}
                className="shrink-0"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  border: on ? "1px solid #00D4FF" : "1px solid #E2E0DC",
                  height: 32,
                  borderRadius: 100,
                  padding: "0 14px",
                  fontFamily: "Manrope",
                  fontSize: 12,
                  fontWeight: on ? 700 : 600,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 pb-32">
        {tab === "dashboard" && <DashboardTab data={data} />}
        {tab === "profile" && <ProfileTab data={data} onSaved={refresh} />}
        {tab === "availability" && <AvailabilityTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "plans" && <PlansTab isNutritionist={isNutritionist} />}
        {tab === "ai-tasks" && <AITasksTab />}
        {tab === "arena" && <ArenaTab />}
        {tab === "community" && <TrainerCommunityTab />}
        {tab === "messages" && <MessagesTab user={user} />}
        {tab === "clients" && isNutritionist && <ClientsTab />}
      </main>
      <BottomNav />
    </div>
  );
}

function StatusScreen({ testid, icon, iconBorder, title, body, primary, secondary }) {
  const navigate = useNavigate();
  return (
    <div className="app-page min-h-screen" data-testid={testid}>
      <header className="sticky top-0 z-20 bg-canvas/85 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="portal-back"
            className="-ml-2 w-10 h-10 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-display text-[20px]">My Trainer Portal</h1>
        </div>
      </header>
      <main className="max-w-md mx-auto px-6 pt-10 pb-32 text-center">
        <div
          className="mx-auto rounded-full grid place-items-center"
          style={{ width: 72, height: 72, border: `2px solid ${iconBorder}` }}
        >
          {icon}
        </div>
        <h2
          className="font-barlow-cond mt-6"
          style={{ fontSize: 34, fontWeight: 900, color: "#1A1A1A", lineHeight: 1.05 }}
        >
          {title}
        </h2>
        <p className="mt-3 text-[14px] text-ink-soft leading-relaxed">{body}</p>
        <div className="mt-6 space-y-2">
          {primary && (
            <button
              onClick={primary.onClick}
              data-testid="portal-status-primary"
              className="w-full h-12 rounded-pill font-bold"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
            >
              {primary.label}
            </button>
          )}
          {secondary && (
            <button
              onClick={secondary.onClick}
              data-testid="portal-status-secondary"
              className="w-full h-12 rounded-pill font-medium border border-line2"
              style={{ borderRadius: 100 }}
            >
              {secondary.label}
            </button>
          )}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

function DashboardTab({ data }) {
  const stats = [
    { label: "Profile views", value: data.profile_views_week, testid: "stat-views" },
    { label: "Saves", value: data.saves, testid: "stat-saves" },
    { label: "Messages", value: data.messages, testid: "stat-messages" },
    { label: "Enquiries", value: data.enquiries, testid: "stat-enquiries" },
  ];
  return (
    <section data-testid="dashboard-tab" className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-[14px] p-4 border border-line2" data-testid={s.testid}>
            <div className="font-barlow-cond text-[28px] font-bold leading-none">{s.value}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1 text-ink-tertiary font-medium">{s.label}</div>
          </div>
        ))}
      </div>
      <ReferralEarningsCard />
    </section>
  );
}

function ReferralEarningsCard() {
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(false);

  useEffect(() => {
    apiClient
      .get("/marketplace/pro/referral-earnings")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  const fp = data?.this_month_fp || 0;
  const empty = fp === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => data && setSheet(true)}
        data-testid="referral-earnings-card"
        className="w-full text-left bg-white rounded-[14px] p-4 border border-line2"
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.14em] font-bold"
              style={{ color: "#00B0D5" }}
            >
              Recommendation revenue · {data?.month_label || "this month"}
            </div>
            <div
              className="font-barlow-cond leading-none mt-1"
              style={{ fontSize: 32, fontWeight: 900, color: "#1A1A1A" }}
            >
              {fp.toLocaleString("en-IN")}
            </div>
            <div className="text-[12px] text-ink-soft mt-0.5">FitPoints</div>
          </div>
          <div
            className="w-10 h-10 rounded-full grid place-items-center shrink-0"
            style={{ background: "rgba(0,212,255,0.14)", color: "#00B0D5" }}
          >
            <ShoppingBag size={18} />
          </div>
        </div>
        {empty ? (
          <p className="text-[13px] text-ink-soft mt-2">
            No referral earnings yet. Pin products to your profile to start.
          </p>
        ) : (
          <p className="text-[12px] text-ink-soft mt-2">
            {data?.this_month_count || 0} purchase{(data?.this_month_count || 0) === 1 ? "" : "s"} — tap to see details
          </p>
        )}
      </button>

      {sheet && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setSheet(false)}
          data-testid="referral-earnings-sheet"
        >
          <div
            className="w-full bg-white rounded-t-[24px] p-5 max-w-2xl mx-auto"
            style={{ maxHeight: "80vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-barlow-cond font-extrabold text-[22px]">
                Referral earnings
              </h3>
              <button
                onClick={() => setSheet(false)}
                aria-label="Close"
                className="w-8 h-8 grid place-items-center rounded-full bg-canvas2"
              >
                ✕
              </button>
            </div>
            <div className="mt-3 overflow-y-auto" style={{ maxHeight: "60vh" }}>
              {(data?.purchases || []).length === 0 && (
                <div className="text-center py-8 text-[13px] text-ink-soft">
                  Nothing here yet.
                </div>
              )}
              {(data?.purchases || []).map((p) => (
                <div
                  key={p.order_id + p.date}
                  className="flex items-center gap-3 py-3 border-b border-line2"
                  data-testid={`earning-row-${p.order_id}`}
                >
                  <div className="w-11 h-11 rounded-[10px] overflow-hidden bg-canvas2 shrink-0">
                    {p.photo && (
                      <img src={p.photo} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold line-clamp-1">
                      {p.product_title}
                    </div>
                    <div className="text-[11px] text-ink-soft">
                      {p.buyer_first_name} · {(p.date || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div
                      className="font-barlow-cond font-black text-[16px]"
                      style={{ color: "#00B0D5" }}
                    >
                      +{p.fp_earned}
                    </div>
                    <div className="text-[10px] text-ink-tertiary">FP</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProfileTab({ data, onSaved }) {
  const [form, setForm] = useState({
    bio: data.profile?.bio || "",
    consultation_type: data.profile?.consultation_type || "online",
    languages: (data.profile?.languages || []).join(", "),
    instagram: data.profile?.instagram || "",
  });
  const [saving, setSaving] = useState(false);
  const [introEnabled, setIntroEnabled] = useState(
    data.profile?.intro_call_enabled !== false // default ON
  );
  const [introSaving, setIntroSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/pro/profile", {
        bio: form.bio,
        consultation_type: form.consultation_type,
        languages: form.languages.split(",").map((l) => l.trim()).filter(Boolean),
        instagram: form.instagram,
      });
      toast.success("Profile updated");
      onSaved?.();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleIntro = async (next) => {
    setIntroSaving(true);
    try {
      await apiClient.put("/pro/intro-tier", { enabled: next });
      setIntroEnabled(next);
      toast.success(next ? "Free intro tier ON" : "Free intro tier OFF");
    } catch {
      toast.error("Could not update intro tier");
    } finally {
      setIntroSaving(false);
    }
  };

  return (
    <section data-testid="profile-tab" className="space-y-4">
      <Field label="Bio (max 250 chars)">
        <textarea
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 250) })}
          data-testid="bio-input"
          className="w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          style={{ minHeight: 90 }}
        />
      </Field>
      <Field label="Consultation type">
        <div className="flex gap-2">
          {[
            { id: "online", label: "Online" },
            { id: "in_person", label: "In-person" },
            { id: "both", label: "Both" },
          ].map((o) => (
            <button
              key={o.id}
              onClick={() => setForm({ ...form, consultation_type: o.id })}
              data-testid={`type-${o.id}`}
              className="flex-1 h-10 rounded-pill text-[13px]"
              style={{
                background: form.consultation_type === o.id ? "#00D4FF" : "#ECEAE7",
                color: form.consultation_type === o.id ? "#0B0B0B" : "#6B6760",
                fontWeight: form.consultation_type === o.id ? 700 : 600,
                borderRadius: 100,
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Languages (comma separated)">
        <input
          value={form.languages}
          onChange={(e) => setForm({ ...form, languages: e.target.value })}
          placeholder="English, Hindi, Marathi"
          data-testid="languages-input"
          className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        />
      </Field>
      <Field label="Instagram (optional)">
        <input
          value={form.instagram}
          onChange={(e) => setForm({ ...form, instagram: e.target.value })}
          placeholder="@yourhandle"
          data-testid="instagram-input"
          className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        />
      </Field>
      <button
        onClick={save}
        disabled={saving}
        data-testid="profile-save"
        className="w-full h-12 rounded-pill disabled:opacity-50"
        style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
      >
        {saving ? "Saving…" : "Save changes"}
      </button>

      {/* Free intro tier toggle — separate row so it feels like a distinct setting */}
      <div
        className="mt-4 flex items-center justify-between p-4 rounded-[14px] border border-line2 bg-white"
        data-testid="intro-tier-toggle"
      >
        <div className="min-w-0 pr-3">
          <div className="font-display text-[15px]">Free 15-min intro call</div>
          <div className="text-[12px] text-ink-soft mt-0.5">Lets new members try you risk-free. One per member.</div>
        </div>
        <button
          onClick={() => toggleIntro(!introEnabled)}
          disabled={introSaving}
          data-testid="intro-tier-switch"
          aria-pressed={introEnabled}
          className="shrink-0"
          style={{
            width: 46, height: 26, borderRadius: 100,
            background: introEnabled ? "#0F766E" : "#CFCDC8",
            position: "relative", transition: "background 120ms",
            border: "none", cursor: "pointer",
          }}
        >
          <span style={{
            position: "absolute", top: 3, left: introEnabled ? 23 : 3,
            width: 20, height: 20, borderRadius: "50%", background: "white",
            transition: "left 120ms",
          }} />
        </button>
      </div>

      <RecommendedProductsEditor />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Recommended Products Editor — verified pros pin up to 3 items
// ─────────────────────────────────────────────────────────────
function RecommendedProductsEditor() {
  const [pinned, setPinned] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadPinned = async () => {
    try {
      const me = await apiClient.get("/professionals/me");
      const uid = me.data?.user_id;
      if (!uid) return;
      const { data } = await apiClient.get(`/marketplace/pro/${uid}/recommended-products`);
      setPinned(data?.products || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadPinned();
  }, []);

  const search = async (q) => {
    setQuery(q);
    if (!q.trim()) return setResults([]);
    try {
      const { data } = await apiClient.get(`/marketplace/products?q=${encodeURIComponent(q)}&size=10`);
      setResults(data?.products || []);
    } catch {
      setResults([]);
    }
  };

  const persist = async (next) => {
    setSaving(true);
    try {
      await apiClient.put("/marketplace/pro/recommended-products", {
        product_ids: next.map((p) => p.product_id),
      });
      setPinned(next);
      toast.success("Updated recommendations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const addPin = (p) => {
    if (pinned.some((x) => x.product_id === p.product_id)) return;
    if (pinned.length >= 3) return toast.error("Max 3 recommendations");
    persist([...pinned, p]);
  };

  const removePin = (pid) => persist(pinned.filter((p) => p.product_id !== pid));

  return (
    <div
      className="mt-6 pt-5 border-t border-line2"
      data-testid="recommended-products-editor"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-barlow-cond font-extrabold text-[18px]">Recommended products</h3>
          <p className="text-[12px] text-ink-soft">
            Pin up to 3 marketplace products to your public profile. Earn +50 FitPoints per purchase.
          </p>
        </div>
        <span className="text-[11px] text-ink-tertiary">{pinned.length}/3</span>
      </div>

      {pinned.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="pinned-list">
          {pinned.map((p) => (
            <div
              key={p.product_id}
              className="flex items-center gap-3 bg-white border border-line2 rounded-[12px] p-2"
            >
              <div className="w-12 h-12 rounded-[8px] overflow-hidden bg-canvas2 shrink-0">
                {(p.photos || [])[0] && (
                  <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold line-clamp-1">{p.title}</div>
                <div className="text-[12px] text-ink-soft">
                  ₹{(p.price_inr || 0).toLocaleString("en-IN")}
                </div>
              </div>
              <button
                onClick={() => removePin(p.product_id)}
                disabled={saving}
                data-testid={`unpin-${p.product_id}`}
                className="text-[12px] font-semibold px-3 h-9 rounded-pill border border-line2"
                style={{ borderRadius: 100 }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {pinned.length < 3 && (
        <>
          <input
            value={query}
            onChange={(e) => search(e.target.value)}
            placeholder="Search marketplace products…"
            data-testid="recommended-search"
            className="mt-3 w-full h-11 px-3 rounded-[12px] border border-line bg-canvas2 outline-none text-[14px]"
          />
          {results.length > 0 && (
            <div className="mt-2 max-h-64 overflow-y-auto space-y-1.5">
              {results.map((p) => (
                <button
                  key={p.product_id}
                  type="button"
                  onClick={() => addPin(p)}
                  data-testid={`rec-candidate-${p.product_id}`}
                  className="w-full text-left flex items-center gap-3 p-2 rounded-[10px] hover:bg-canvas2"
                >
                  <div className="w-10 h-10 rounded-[8px] overflow-hidden bg-canvas2 shrink-0">
                    {(p.photos || [])[0] && (
                      <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold line-clamp-1">{p.title}</div>
                    <div className="text-[11px] text-ink-soft">
                      ₹{(p.price_inr || 0).toLocaleString("en-IN")} · {p.category}
                    </div>
                  </div>
                  <span className="text-[12px] font-bold" style={{ color: "#00B0D5" }}>
                    Pin
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AvailabilityTab() {
  const [form, setForm] = useState({
    durations: [60],
    days: ["mon", "wed", "fri"],
    start_time: "07:00",
    end_time: "20:00",
    session_type: "online",
    rate_inr: 0,
  });
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d],
    }));
  };
  const toggleDuration = (d) => {
    setForm((f) => ({
      ...f,
      durations: f.durations.includes(d) ? f.durations.filter((x) => x !== d) : [...f.durations, d],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/pro/availability", form);
      // Also publish a derived slot grid for self-serve booking
      const slots = [];
      const startH = parseInt((form.start_time || "07:00").split(":")[0], 10);
      const endH = parseInt((form.end_time || "20:00").split(":")[0], 10);
      const dur = form.durations[0] || 60;
      for (const day of form.days) {
        for (let h = startH; h + dur / 60 <= endH; h += dur / 60) {
          const hh = String(Math.floor(h)).padStart(2, "0");
          const mm = h % 1 === 0 ? "00" : "30";
          slots.push({ day, time: `${hh}:${mm}` });
        }
      }
      await apiClient.put("/pro/calendar", {
        slots,
        repeat_weekly: true,
        buffer_minutes: 15,
        duration_minutes: dur,
        rate_inr: form.rate_inr || 500,
      });
      toast.success("Availability updated. Self-serve slots published.");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="availability-tab" className="space-y-4">
      <Field label="Session durations">
        <div className="flex gap-2">
          {[30, 45, 60].map((d) => {
            const on = form.durations.includes(d);
            return (
              <button
                key={d}
                onClick={() => toggleDuration(d)}
                data-testid={`duration-${d}`}
                className="flex-1 h-10 rounded-pill text-[13px]"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  fontWeight: on ? 700 : 600,
                  borderRadius: 100,
                }}
              >
                {d} min
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Available days">
        <div className="flex gap-1.5">
          {DAYS.map((d) => {
            const on = form.days.includes(d);
            return (
              <button
                key={d}
                onClick={() => toggleDay(d)}
                data-testid={`day-${d}`}
                className="flex-1 h-10 capitalize text-[12px] rounded-pill"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  fontWeight: on ? 700 : 600,
                  borderRadius: 100,
                }}
              >
                {d.slice(0, 1).toUpperCase()}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start time">
          <input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            data-testid="start-time"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          />
        </Field>
        <Field label="End time">
          <input
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            data-testid="end-time"
            className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          />
        </Field>
      </div>
      <Field label="Rate per session in INR (optional — leave blank for 'Rate on request')">
        <input
          type="number"
          value={form.rate_inr || ""}
          onChange={(e) => setForm({ ...form, rate_inr: e.target.value ? Number(e.target.value) : null })}
          data-testid="rate-input"
          className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none"
        />
      </Field>
      <button
        onClick={save}
        disabled={saving}
        data-testid="availability-save"
        className="w-full h-12 rounded-pill disabled:opacity-50"
        style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
      >
        {saving ? "Saving…" : "Save availability"}
      </button>
    </section>
  );
}

function MessagesTab({ user }) {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    apiClient.get("/pro/threads").then((r) => setThreads(r.data || [])).catch(() => {});
  }, []);

  const open = async (t) => {
    setActive(t);
    const { data } = await apiClient.get(`/pro/threads/${t.thread_id}`);
    setMessages(data.messages || []);
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    try {
      const { data } = await apiClient.post(`/pro/threads/${active.thread_id}/messages`, {
        text: draft.trim(),
      });
      setMessages((m) => [...m, data]);
      setDraft("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "contact_blocked") {
        toast.error(detail.message || "Contact details cannot be shared here.");
      } else {
        toast.error("Could not send");
      }
    }
  };

  if (active) {
    return (
      <section data-testid="thread-detail">
        <button
          onClick={() => setActive(null)}
          className="text-[13px] text-ink-soft mb-3"
          data-testid="thread-back"
        >
          ← Back to inbox
        </button>
        <div className="bg-white rounded-[16px] p-3 border border-line2 max-h-[60vh] overflow-y-auto space-y-2">
          {messages.map((m) => {
            const mine = m.sender_user_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] px-3 py-2 rounded-[14px] text-[13px]"
                  style={{
                    background: mine ? "#00D4FF" : "#ECEAE7",
                    color: mine ? "#000" : "var(--text-primary)",
                  }}
                >
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>
        <ContactLeakBanner rule={detectContactLeak(draft)} />
        <div className="flex gap-2 mt-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type a message…"
            data-testid="thread-input"
            className="flex-1 h-12 px-3 rounded-pill border border-line bg-canvas2 text-[14px] outline-none"
          />
          <button
            onClick={send}
            disabled={!!detectContactLeak(draft)}
            data-testid="thread-send"
            className="px-4 h-12 rounded-pill disabled:opacity-50"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
          >
            Send
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="messages-tab" className="space-y-2">
      <button
        type="button"
        onClick={() => navigate("/inbox?tab=marketplace")}
        data-testid="open-marketplace-inbox"
        className="w-full flex items-center justify-between bg-white rounded-[12px] p-3 border border-line2 text-left"
      >
        <div>
          <div className="text-[13px] font-semibold">Buyers & Sellers inbox</div>
          <div className="text-[11px] text-ink-soft">Marketplace message threads</div>
        </div>
        <span className="text-[12px] font-bold" style={{ color: "#00B0D5" }}>
          Open →
        </span>
      </button>
      {threads.length === 0 && (
        <div className="text-center py-10 text-ink-soft text-[13px]">No trainer messages yet.</div>
      )}
      {threads.map((t) => (
        <button
          key={t.thread_id}
          onClick={() => open(t)}
          data-testid={`thread-${t.thread_id}`}
          className="w-full flex items-center gap-3 bg-white rounded-[12px] p-3 border border-line2 text-left"
        >
          <div className="w-9 h-9 rounded-full grid place-items-center text-[12px] font-medium" style={{ background: "var(--accent-blue-dim)" }}>
            {(t.member_name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[14px]">{t.member_name}</div>
            <div className="text-[12px] text-ink-soft truncate">{t.last_message_preview}</div>
          </div>
          {t.unread_for_pro && <span className="w-2 h-2 rounded-full" style={{ background: "#00D4FF" }} />}
        </button>
      ))}
    </section>
  );
}

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [activeMember, setActiveMember] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    apiClient.get("/nutrition/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const open = async (m) => {
    setActiveMember(m);
    const { data } = await apiClient.get(`/nutrition/clients/${m.user_id}/logs?days=30`);
    setLogs(data || []);
  };

  if (activeMember) {
    return (
      <section data-testid="client-logs">
        <button onClick={() => setActiveMember(null)} className="text-[13px] text-ink-soft mb-3">
          ← Back to clients
        </button>
        <h3 className="font-display text-[20px]">{activeMember.name}'s Food Log</h3>
        <p className="text-[12px] text-ink-soft">Read-only · Last 30 days</p>
        <div className="space-y-2 mt-3">
          {logs.map((l) => (
            <div key={l.date} className="bg-white rounded-[12px] p-3 border border-line2" data-testid={`client-log-${l.date}`}>
              <div className="text-[13px] font-medium">{l.date}</div>
              <div className="text-[11px] text-ink-soft">
                {l.totals?.calories || 0} kcal · {l.totals?.protein_g || 0}g P
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="clients-tab" className="space-y-2">
      {clients.length === 0 && (
        <div className="text-center py-10 text-ink-soft text-[13px]">
          No clients yet. Members must grant you access to their food log.
        </div>
      )}
      {clients.map((c) => (
        <button
          key={c.user_id}
          onClick={() => open(c)}
          data-testid={`client-${c.user_id}`}
          className="w-full flex items-center gap-3 bg-white rounded-[12px] p-3 border border-line2 text-left"
        >
          <div className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "var(--accent-blue-dim)" }}>
            {(c.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[14px]">{c.name}</div>
            <div className="text-[11px] text-ink-soft">Granted {(c.granted_at || "").slice(0, 10)}</div>
          </div>
          <span className="text-[12px] underline">View Food Log</span>
        </button>
      ))}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Bookings tab — incoming session requests + upcoming sessions.
// Previously a trainer had no way to see a paid booking existed at all
// except a client messaging them directly. This is also the "owner's end"
// verification step: a paid booking is not a confirmed session until the
// trainer explicitly accepts it here.
// ──────────────────────────────────────────────────────────
function BookingsTab() {
  const [pending, setPending] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);

  const load = () => {
    apiClient
      .get("/pro/trainer/bookings")
      .then((r) => {
        setPending(r.data?.pending || []);
        setUpcoming(r.data?.upcoming || []);
      })
      .catch(() => toast.error("Couldn't load bookings"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const respond = async (bookingId, action) => {
    setRespondingId(bookingId);
    try {
      await apiClient.post(`/trainers/bookings/${bookingId}/respond`, { action });
      toast.success(action === "accept" ? "Session accepted" : "Session declined");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't respond to this booking");
    } finally {
      setRespondingId(null);
    }
  };

  const fmtWhen = (iso) => {
    if (!iso) return "Time to be confirmed";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return <div className="text-center text-ink-soft py-10" data-testid="bookings-loading">Loading…</div>;
  }

  return (
    <section data-testid="bookings-tab" className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-bold mb-2">
          Needs your response ({pending.length})
        </div>
        {pending.length === 0 ? (
          <div className="bg-white border border-line2 rounded-[14px] p-5 text-center text-ink-soft text-[13px]" data-testid="bookings-pending-empty">
            No new session requests right now.
          </div>
        ) : (
          <div className="space-y-2.5">
            {pending.map((b) => (
              <div key={b.booking_id} className="bg-white border border-line2 rounded-[14px] p-4" data-testid={`booking-pending-${b.booking_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[14px]">{b.member_name}</span>
                  <span className="font-barlow-cond text-[16px] font-extrabold">
                    {b.amount_inr > 0 ? `₹${b.amount_inr}` : "Free intro"}
                  </span>
                </div>
                <div className="text-[12px] text-ink-soft mt-1">{fmtWhen(b.session_at)}</div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => respond(b.booking_id, "decline")}
                    disabled={respondingId === b.booking_id}
                    data-testid={`booking-decline-${b.booking_id}`}
                    className="flex-1 h-10 rounded-pill text-[13px] font-semibold disabled:opacity-50"
                    style={{ border: "1px solid #E5484D", color: "#E5484D", borderRadius: 100 }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={() => respond(b.booking_id, "accept")}
                    disabled={respondingId === b.booking_id}
                    data-testid={`booking-accept-${b.booking_id}`}
                    className="flex-1 h-10 rounded-pill text-[13px] font-bold disabled:opacity-50"
                    style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
                  >
                    Accept
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-bold mb-2">
          Upcoming sessions ({upcoming.length})
        </div>
        {upcoming.length === 0 ? (
          <div className="bg-white border border-line2 rounded-[14px] p-5 text-center text-ink-soft text-[13px]" data-testid="bookings-upcoming-empty">
            No confirmed sessions yet.
          </div>
        ) : (
          <div className="space-y-2.5">
            {upcoming.map((b) => (
              <div key={b.booking_id} className="bg-white border border-line2 rounded-[14px] p-4" data-testid={`booking-upcoming-${b.booking_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[14px]">{b.member_name}</span>
                  <span className="font-barlow-cond text-[16px] font-extrabold">
                    {b.amount_inr > 0 ? `₹${b.amount_inr}` : "Free intro"}
                  </span>
                </div>
                <div className="text-[12px] mt-1 font-medium" style={{ color: "#0B8477" }}>
                  {fmtWhen(b.session_at)} · Confirmed
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────
// Plans tab — trainer plan library (workout & meal)
// ──────────────────────────────────────────────────────────
function PlansTab({ isNutritionist = false }) {
  const [plans, setPlans] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);

  const refresh = () => apiClient.get("/pro/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await apiClient.delete(`/pro/plans/${id}`);
    refresh();
  };

  const heading = isNutritionist ? "My Meal Plans" : "My Workout Plans";
  const emptyBody = isNutritionist
    ? "No meal plans yet. Build your first nutrition plan and send it to clients."
    : "No workout plans yet. Create your first plan to send to clients.";

  return (
    <section data-testid="plans-tab" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-barlow-cond font-extrabold text-[20px]">{heading}</h3>
        <button onClick={() => setShowBuilder(true)} data-testid="new-plan-btn" className="h-9 px-3 rounded-pill text-[12.5px] font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>
          {isNutritionist ? "+ New meal plan" : "+ New plan"}
        </button>
      </div>
      {plans.length === 0 && (
        <div className="bg-white border border-line2 rounded-[12px] p-5 text-center text-[13px] text-ink-soft">{emptyBody}</div>
      )}
      <div className="space-y-2">
        {plans.map((p) => (
          <div key={p.plan_id} className="bg-white border border-line2 rounded-[12px] p-3 flex items-center gap-3" data-testid={`plan-row-${p.plan_id}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 h-5 leading-5 rounded-pill" style={{ background: p.plan_type === "workout" ? "rgba(0,212,255,0.18)" : "rgba(15,118,110,0.15)", color: p.plan_type === "workout" ? "#00B0D5" : "#0F766E", borderRadius: 100 }}>
              {p.plan_type}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[14px] truncate">{p.name}</div>
              <div className="text-[11px] text-ink-tertiary">
                {(p.created_at || "").slice(0, 10)}
                {p.plan_type === "nutrition" && p.daily_calories ? ` · ${p.daily_calories} kcal target` : ""}
              </div>
            </div>
            <SendPlanButton plan={p} onSent={refresh} />
            <button onClick={() => remove(p.plan_id)} aria-label="Delete" data-testid={`del-plan-${p.plan_id}`} className="text-red-500 text-[11px]">Delete</button>
          </div>
        ))}
      </div>
      {showBuilder && (
        <PlanBuilder
          onClose={() => setShowBuilder(false)}
          onSaved={() => { setShowBuilder(false); refresh(); }}
          defaultPlanType={isNutritionist ? "nutrition" : "workout"}
        />
      )}
    </section>
  );
}

function SendPlanButton({ plan, onSent }) {
  const [open, setOpen] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [note, setNote] = useState("");
  const send = async () => {
    if (!memberId) { toast.error("Enter a member user_id"); return; }
    try {
      await apiClient.post(`/pro/plans/${plan.plan_id}/send`, { member_user_id: memberId, note_to_member: note || null });
      toast.success("Plan sent");
      setOpen(false);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`send-plan-${plan.plan_id}`} className="text-[11px] h-7 px-2.5 rounded-pill font-bold" style={{ background: "#1A1A1A", color: "#fff", borderRadius: 100 }}>Send</button>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-[20px] p-5 w-full max-w-sm m-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-[18px] mb-2">Send "{plan.name}"</h3>
            <p className="text-[12.5px] text-ink-soft mb-3">Enter the member's user_id (find it in their profile or recent enquiry).</p>
            <input value={memberId} onChange={(e) => setMemberId(e.target.value.trim())} placeholder="member user_id" data-testid="send-member-id" className="w-full h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[13px] font-mono" />
            <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 240))} placeholder="Optional note to the member" rows={3} data-testid="send-note" className="w-full mt-2 p-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[13px]" />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setOpen(false)} className="flex-1 h-11 rounded-pill border border-line2" style={{ borderRadius: 100 }}>Cancel</button>
              <button onClick={send} data-testid="send-confirm" className="flex-1 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Send</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PlanBuilder({ onClose, onSaved, defaultPlanType = "workout" }) {
  const [planType, setPlanType] = useState(defaultPlanType);
  const [name, setName] = useState("");
  const [workoutDays, setWorkoutDays] = useState([{ day_label: "Day 1", exercises: [] }]);
  const [mealDays, setMealDays] = useState([{ day_label: "Day 1", breakfast: "", lunch: "", dinner: "", snacks: "", daily_calories: null }]);
  // Nutrition daily targets — these override the member's computed Mifflin goal.
  const [dailyCalories, setDailyCalories] = useState("");
  const [proteinG, setProteinG] = useState("");
  const [carbsG, setCarbsG] = useState("");
  const [fatG, setFatG] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const addWorkoutDay = () => setWorkoutDays([...workoutDays, { day_label: `Day ${workoutDays.length + 1}`, exercises: [] }]);
  const addExercise = (di) => setWorkoutDays(workoutDays.map((d, i) => i === di ? { ...d, exercises: [...d.exercises, { name: "", sets: 3, reps: "10", rest_seconds: 60, weight: "" }] } : d));
  const setEx = (di, ei, k, v) => setWorkoutDays(workoutDays.map((d, i) => i === di ? { ...d, exercises: d.exercises.map((e, j) => j === ei ? { ...e, [k]: v } : e) } : d));
  const rmEx = (di, ei) => setWorkoutDays(workoutDays.map((d, i) => i === di ? { ...d, exercises: d.exercises.filter((_, j) => j !== ei) } : d));
  const addMealDay = () => setMealDays([...mealDays, { day_label: `Day ${mealDays.length + 1}`, breakfast: "", lunch: "", dinner: "", snacks: "", daily_calories: null }]);
  const setMeal = (di, k, v) => setMealDays(mealDays.map((d, i) => i === di ? { ...d, [k]: v } : d));

  const save = async () => {
    if (name.length < 2) { toast.error("Name your plan"); return; }
    const isNutrition = planType !== "workout";
    if (isNutrition && !dailyCalories) {
      toast.error("Set a daily calorie target");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/pro/plans", {
        // Backend accepts both "meal" and "nutrition". Send the canonical
        // "nutrition" keyword so future code can drop the legacy alias.
        plan_type: isNutrition ? "nutrition" : "workout",
        name,
        weeks: 1,
        duration_days: 7,
        workout_days: !isNutrition ? workoutDays : [],
        meal_days: isNutrition ? mealDays : [],
        daily_calories: isNutrition && dailyCalories ? Number(dailyCalories) : null,
        protein_g: isNutrition && proteinG ? Number(proteinG) : null,
        carbs_g: isNutrition && carbsG ? Number(carbsG) : null,
        fat_g: isNutrition && fatG ? Number(fatG) : null,
        notes: notes || null,
      });
      toast.success("Plan saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end bg-black/40" onClick={onClose} data-testid="plan-builder">
      <div className="w-full bg-white rounded-t-[20px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-line2 px-4 py-3 flex items-center justify-between">
          <h3 className="font-display text-[18px]">New plan</h3>
          <button onClick={onClose} aria-label="Close" className="text-ink-tertiary">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            {[["workout","Workout"],["nutrition","Nutrition"]].map(([id, l]) => {
              const on = planType === id;
              return <button key={id} onClick={() => setPlanType(id)} data-testid={`plan-type-${id}`} className="flex-1 h-10 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{l}</button>;
            })}
          </div>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} placeholder="Plan name" data-testid="plan-name" className="w-full h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />

          {planType === "workout" && (
            <div className="space-y-3">
              {workoutDays.map((d, di) => (
                <div key={di} className="bg-canvas2 rounded-[12px] p-3">
                  <input value={d.day_label} onChange={(e) => setWorkoutDays(workoutDays.map((x, i) => i === di ? { ...x, day_label: e.target.value } : x))} className="w-full font-bold text-[14px] bg-transparent outline-none mb-2" />
                  {d.exercises.map((e, ei) => (
                    <div key={ei} className="bg-white rounded-[10px] p-2 mb-2">
                      <input value={e.name} onChange={(ev) => setEx(di, ei, "name", ev.target.value)} placeholder="Exercise name (e.g. Squat)" data-testid={`ex-name-${di}-${ei}`} className="w-full h-9 px-2 rounded-[8px] border border-line outline-none text-[13px]" />
                      <div className="grid grid-cols-4 gap-1 mt-1.5">
                        <input type="number" value={e.sets} onChange={(ev) => setEx(di, ei, "sets", Number(ev.target.value))} placeholder="Sets" className="h-9 px-2 rounded-[8px] border border-line outline-none text-[12.5px]" />
                        <input value={e.reps} onChange={(ev) => setEx(di, ei, "reps", ev.target.value)} placeholder="Reps" className="h-9 px-2 rounded-[8px] border border-line outline-none text-[12.5px]" />
                        <input value={e.weight || ""} onChange={(ev) => setEx(di, ei, "weight", ev.target.value)} placeholder="Weight" className="h-9 px-2 rounded-[8px] border border-line outline-none text-[12.5px]" />
                        <input type="number" value={e.rest_seconds} onChange={(ev) => setEx(di, ei, "rest_seconds", Number(ev.target.value))} placeholder="Rest s" className="h-9 px-2 rounded-[8px] border border-line outline-none text-[12.5px]" />
                      </div>
                      <button onClick={() => rmEx(di, ei)} className="mt-1 text-[11px] text-red-500">Remove</button>
                    </div>
                  ))}
                  <button onClick={() => addExercise(di)} data-testid={`add-ex-${di}`} className="w-full h-9 rounded-pill border border-dashed border-line2 text-[12.5px] font-medium" style={{ borderRadius: 100 }}>+ Add exercise</button>
                </div>
              ))}
              <button onClick={addWorkoutDay} className="w-full h-10 rounded-pill border border-line2 text-[13px] font-medium" style={{ borderRadius: 100 }}>+ Add day</button>
            </div>
          )}

          {planType !== "workout" && (
            <div className="space-y-3">
              {/* Daily targets — override member's computed Mifflin goal */}
              <div className="bg-canvas2 rounded-[12px] p-3 space-y-2" data-testid="nutrition-targets">
                <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "#0F766E" }}>Daily targets (required)</div>
                <input
                  type="number"
                  value={dailyCalories}
                  onChange={(e) => setDailyCalories(e.target.value)}
                  placeholder="Daily calories · e.g. 1800"
                  data-testid="plan-daily-calories"
                  className="w-full h-10 px-2 rounded-[8px] border border-line outline-none text-[13px] bg-white"
                />
                <div className="grid grid-cols-3 gap-1.5">
                  <input type="number" value={proteinG} onChange={(e) => setProteinG(e.target.value)} placeholder="Protein g" data-testid="plan-protein-g" className="h-10 px-2 rounded-[8px] border border-line outline-none text-[13px] bg-white" />
                  <input type="number" value={carbsG} onChange={(e) => setCarbsG(e.target.value)} placeholder="Carbs g" data-testid="plan-carbs-g" className="h-10 px-2 rounded-[8px] border border-line outline-none text-[13px] bg-white" />
                  <input type="number" value={fatG} onChange={(e) => setFatG(e.target.value)} placeholder="Fat g" data-testid="plan-fat-g" className="h-10 px-2 rounded-[8px] border border-line outline-none text-[13px] bg-white" />
                </div>
                <p className="text-[11px] text-ink-soft">These override the member's home-screen calorie ring when this plan is active.</p>
              </div>

              {mealDays.map((d, di) => (
                <div key={di} className="bg-canvas2 rounded-[12px] p-3 space-y-2">
                  <input value={d.day_label} onChange={(e) => setMeal(di, "day_label", e.target.value)} className="w-full font-bold text-[14px] bg-transparent outline-none" />
                  {[["breakfast","Breakfast"],["lunch","Lunch"],["dinner","Dinner"],["snacks","Snacks"]].map(([k, l]) => (
                    <textarea key={k} value={d[k]} onChange={(e) => setMeal(di, k, e.target.value.slice(0, 200))} placeholder={l} rows={2} className="w-full p-2 rounded-[8px] border border-line outline-none text-[13px] bg-white" />
                  ))}
                </div>
              ))}
              <button onClick={addMealDay} className="w-full h-10 rounded-pill border border-line2 text-[13px] font-medium" style={{ borderRadius: 100 }}>+ Add day</button>
            </div>
          )}

          <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 300))} placeholder="Notes for the member (optional)" rows={2} className="w-full p-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[13px]" />

          <button onClick={save} disabled={busy} data-testid="plan-save" className="w-full h-12 rounded-pill font-bold disabled:opacity-50" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>{busy ? "Saving…" : "Save plan"}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Trainer Community Tab — manage FitTips
// ─────────────────────────────────────────────────────────────────
function TrainerCommunityTab() {
  const [tips, setTips] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/fittips/by-trainer/me");
      setTips(data || []);
    } catch {
      setTips([]);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this tip? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/fittips/${id}`);
      toast.success("Tip removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <section className="space-y-4" data-testid="trainer-community-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[20px]">FitTips</h2>
          <p className="text-[12px] text-ink-soft">Share expert tips with FitFrik members. +25 FP per tip, +5 FP per save.</p>
        </div>
        <button
          onClick={() => setComposeOpen(true)}
          data-testid="post-fittip-btn"
          className="px-4 h-10 rounded-pill"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700, fontSize: 13 }}
        >
          + Post a FitTip
        </button>
      </div>

      {tips.length === 0 ? (
        <div className="text-center py-10 px-4 bg-white rounded-[14px] border border-line2">
          <div className="font-display text-[18px]">No tips yet</div>
          <p className="text-[13px] text-ink-soft mt-1">Drop your first expert tip — appears in member feeds within minutes.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tips.map((t) => (
            <div key={t.tip_id} className="bg-white rounded-[14px] p-3 border border-line2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#A05E00" }}>
                  {t.category}
                </span>
                <button
                  onClick={() => remove(t.tip_id)}
                  data-testid={`fittip-remove-${t.tip_id}`}
                  className="text-[12px]"
                  style={{ color: "#A52121", fontWeight: 600, background: "transparent", border: "none" }}
                >
                  Delete
                </button>
              </div>
              <p className="text-[14px] mt-1.5 whitespace-pre-wrap">{t.tip_text}</p>
              {t.image_url && (
                <img src={t.image_url} alt="" style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 10, marginTop: 8 }} />
              )}
              <div className="text-[11px] text-ink-soft mt-2">{t.saves_count || 0} saves</div>
            </div>
          ))}
        </div>
      )}

      {composeOpen && <FitTipComposer onClose={() => setComposeOpen(false)} onPosted={() => { setComposeOpen(false); load(); }} />}
    </section>
  );
}

function FitTipComposer({ onClose, onPosted }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("workout");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    try {
      const compressed = await compressImage(file);
      setImage(compressed);
    } catch {
      toast.error("Could not load image");
    }
  };

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await apiClient.post("/fittips", {
        tip_text: text.trim(),
        category,
        image_url: image,
      });
      toast.success("Posted · +25 FitPoints");
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const counterColor = text.length >= 140 ? "#EF4444" : text.length >= 120 ? "#F59E0B" : "#6B6760";

  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" data-testid="fittip-composer">
      <div className="w-full bg-white rounded-t-[20px] p-5" style={{ maxHeight: "92vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[20px]">Post a FitTip</h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full" style={{ background: "#F2F0EC", border: "none" }} aria-label="Close">×</button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 140))}
          placeholder="Share something useful in 140 chars or less…"
          data-testid="fittip-text"
          className="w-full mt-3 p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          style={{ minHeight: 110 }}
        />
        <div className="text-right text-[11px]" style={{ color: counterColor }}>{text.length}/140</div>
        <div className="flex gap-2 flex-wrap mt-2">
          {["workout", "nutrition", "recovery", "form", "mindset"].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`fittip-cat-${c}`}
              className="px-3 h-9 rounded-pill text-[12px] font-bold"
              style={{
                background: category === c ? "#0C0C0B" : "transparent",
                color: category === c ? "white" : "var(--text-secondary, #4A4845)",
                border: "1px solid",
                borderColor: category === c ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))",
                borderRadius: 100,
                textTransform: "capitalize",
              }}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => cameraRef.current?.click()} data-testid="fittip-camera"
            className="flex-1 h-11 rounded-[12px] grid place-items-center" style={{ background: "#F2F0EC", border: "1px solid #E2E0DC" }}>
            <Camera size={18} />
          </button>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <button onClick={() => galleryRef.current?.click()} data-testid="fittip-gallery"
            className="flex-1 h-11 rounded-[12px] grid place-items-center" style={{ background: "#F2F0EC", border: "1px solid #E2E0DC" }}>
            <ImageIcon size={18} />
          </button>
          <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        </div>
        {image && (
          <div className="relative mt-3">
            <img src={image} alt="" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 12 }} />
            <button onClick={() => setImage(null)} aria-label="Remove image"
              className="absolute top-2 right-2 grid place-items-center"
              style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "white", border: "none" }}>×</button>
          </div>
        )}
        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid="fittip-submit"
          className="w-full mt-4 h-12 rounded-pill font-bold disabled:opacity-50"
          style={{ background: "#F5C518", color: "#0C0C0B", borderRadius: 100 }}
        >
          {busy ? "Posting…" : "Post Tip"}
        </button>
      </div>
    </div>
  );
}

function AITasksTab() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => apiClient.get("/pro/trainer-tasks").then((r) => setTasks(r.data?.tasks || []));
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="ai-tasks-tab" className="space-y-3">
      <div>
        <h2 className="font-barlow-cond text-[20px] font-extrabold">AI Trainer tasks</h2>
        <p className="text-[12px] text-ink-soft">Members with persistent form issues. Send a personalised tip — earn +30 FP per response.</p>
      </div>
      {tasks.length === 0 ? (
        <p className="text-[13px] text-ink-soft py-6 text-center">No tasks yet. AI Trainer assigns them when a member shows the same form issue 3+ times in 7 days.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <button key={t.task_id} onClick={() => setActive(t)} data-testid={`task-${t.task_id}`}
              className="w-full text-left p-3 bg-white border border-line2 rounded-[14px]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#ECEAE7] grid place-items-center text-[12px] font-bold">
                  {(t.member_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold">{t.member_name || "Member"}</div>
                  <div className="text-[11px] text-ink-soft">issue: {t.persistent_issue}</div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: t.status === "completed" ? "#16A34A" : "#A37B00" }}>{t.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {active && <RespondTaskModal task={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
    </section>
  );
}

function RespondTaskModal({ task, onClose, onSaved }) {
  const [text, setText] = useState(task.response_text || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error("Type a response");
    setBusy(true);
    try {
      await apiClient.post(`/pro/trainer-tasks/${task.task_id}/respond`, { response_text: text });
      toast.success("Response sent · +30 FP");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="task-respond-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <h3 className="font-barlow-cond text-[22px] font-extrabold">Respond to {task.member_name}</h3>
        <p className="text-[12px] text-ink-soft mt-1">Issue: <strong>{task.persistent_issue}</strong></p>
        {task.context && <p className="text-[11px] text-ink-soft mt-2 italic">"{task.context}"</p>}
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 600))} placeholder="Type a personal tip…" rows={5} data-testid="task-response-text"
          className="w-full mt-3 p-3 bg-[#ECEAE7] border-0 rounded-[12px] text-[14px] outline-none resize-none" />
        <button onClick={submit} disabled={busy} data-testid="task-respond-submit"
          className="w-full mt-3 h-12 bg-black text-white rounded-full font-bold text-[14px]">
          {busy ? "Sending…" : "Send response · +30 FP"}
        </button>
      </div>
    </div>
  );
}

function ArenaTab() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => apiClient.get("/arena/trainer/my-arena").then((r) => setData(r.data)).catch(() => setData({ gym: null, assigned_members: [] }));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-[13px] text-ink-soft py-6 text-center">Loading…</div>;
  if (!data.gym) {
    return (
      <section data-testid="arena-tab" className="space-y-3">
        <h2 className="font-barlow-cond text-[20px] font-extrabold">Arena</h2>
        <p className="text-[13px] text-ink-soft">You're not yet listed at any FitFrik Arena gym. Ask the gym owner to add you as a trainer in their Arena Dashboard.</p>
      </section>
    );
  }
  const g = data.gym;
  return (
    <section data-testid="arena-tab" className="space-y-3">
      <div>
        <h2 className="font-barlow-cond text-[20px] font-extrabold">{g.gym_name} <span style={{ color: "#A37B00" }}>Arena</span></h2>
        <p className="text-[12px] text-ink-soft">{g.city}{g.address ? ` · ${g.address.slice(0, 50)}` : ""}</p>
      </div>
      <div className="bg-white border border-line2 rounded-[14px] p-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-ink-soft">Members assigned to you</div>
        {data.assigned_members?.length ? (
          <div className="mt-2 space-y-2">
            {data.assigned_members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 p-2">
                <div className="w-8 h-8 rounded-full bg-[#ECEAE7] grid place-items-center text-[12px] font-bold">{(m.name || "?").slice(0, 1).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold">{m.name || "Member"}</div>
                  <div className="text-[10px] text-ink-soft">{m.membership_status}</div>
                </div>
                <button onClick={() => setActive(m)} data-testid={`arena-note-${m.user_id}`}
                  className="px-3 py-1 text-[11px] font-bold rounded-full bg-black text-white">Send note</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-ink-soft mt-2">No members assigned yet. Members appear here once the gym owner assigns them in Arena Dashboard.</p>
        )}
      </div>
      {active && <SendArenaNoteModal member={active} onClose={() => setActive(null)} onSent={() => { setActive(null); toast.success("Note sent"); }} />}
    </section>
  );
}

function SendArenaNoteModal({ member, onClose, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error("Type a note");
    setBusy(true);
    try {
      await apiClient.post("/arena/trainer/note", { member_id: member.user_id, note_text: text });
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="arena-note-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <h3 className="font-barlow-cond text-[22px] font-extrabold">Note for {member.name}</h3>
        <p className="text-[12px] text-ink-soft mt-1">Appears on their Home screen today (max 280 chars).</p>
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 280))} placeholder="e.g. Great form on squats yesterday — push 5kg more next session." rows={4} data-testid="arena-note-text"
          className="w-full mt-3 p-3 bg-[#ECEAE7] border-0 rounded-[12px] text-[14px] outline-none resize-none" />
        <div className="text-right text-[10px] text-ink-soft">{text.length}/280</div>
        <button onClick={submit} disabled={busy} data-testid="arena-note-submit"
          className="w-full mt-2 h-12 bg-black text-white rounded-full font-bold text-[14px]">
          {busy ? "Sending…" : "Send note"}
        </button>
      </div>
    </div>
  );
}



